import {
  Box,
  Container,
  Loader,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { useQuery } from "@apollo/client";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Brand, Product, User } from "../../../gql/__generated__/graphql";
import { GET_ALL_PRODUCTS_FOR_BRAND } from "../../apis/lambda/GraphQL/Product/queries";
import { setupCompleteAtom, userInfoAtom } from "../../atoms";
import ProductCard from "../../components/Cards/ProductCard";
import ReviewCard from "../../components/Cards/ReviewCard/ReviewCard";
import BrandEditForm from "./components/BrandEditForm";
import ProfileEditForm from "./components/ProfileEditForm";
import ProfileHeader from "./components/ProfileHeader";
import ProfileInfoPanel from "./components/ProfileInfoPanel";
import { useProfile } from "./components/useProfile";

const ProfilePage = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // React State
  const [isMyProfile, setIsMyProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Jotai State
  const userInfo = useAtomValue(userInfoAtom);
  const setSetupComplete = useSetAtom(setupCompleteAtom);

  const { profile, loading, updateProfile, updateBrand } = useProfile(id);

  // Fetch products for brand profiles
  const { data: productsData, loading: productsLoading } = useQuery(
    GET_ALL_PRODUCTS_FOR_BRAND,
    {
      variables: { brandId: profile?.brandId || "" },
      skip: !profile?.brandId,
    }
  );

  useEffect(() => {
    // Check if this is the user's own profile (either by email or brandId)
    const isOwnProfile =
      userInfo?.email === id || (userInfo?.brandId && userInfo.brandId === id);

    if (isOwnProfile) {
      setIsMyProfile(true);

      // Check if edit query param is present and user is authorized
      const editParam = searchParams.get("edit");
      if (editParam === "true") {
        setIsEditing(true);
        // Remove the query param after setting the state
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    } else {
      setIsMyProfile(false);
    }
  }, [userInfo?.email, userInfo?.brandId, id, searchParams, setSearchParams]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleProfileUpdate = async (updatedProfile: User) => {
    try {
      await updateProfile(updatedProfile);

      // Check if profile is now complete
      const isComplete =
        updatedProfile.validated &&
        !!updatedProfile.name &&
        updatedProfile.name.trim() !== "" &&
        !!updatedProfile.description &&
        updatedProfile.description.trim() !== "";

      // Update the setupComplete atom to trigger banner visibility changes
      setSetupComplete(isComplete);

      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
      // You can add toast notification here to show error to user
    }
  };

  const handleBrandUpdate = async (updatedBrand: Brand) => {
    try {
      await updateBrand(updatedBrand);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update brand:", error);
      // You can add toast notification here to show error to user
    }
  };

  const { averageRating, numReviews } = useMemo(() => {
    if (!profile?.reviews || profile.reviews.length === 0) {
      return { averageRating: 0, numReviews: 0 };
    }
    const totalRating = profile.reviews.reduce(
      (acc, review) => acc + review.rating,
      0
    );
    return {
      averageRating: totalRating / profile.reviews.length,
      numReviews: profile.reviews.length,
    };
  }, [profile?.reviews]);

  const numProducts = useMemo(() => {
    return productsData?.getAllProductsForBrand?.length || 0;
  }, [productsData]);

  if (loading) {
    return (
      <Container className="center" size="xl">
        <Loader />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container size="xl">
        <Box ta="center" py="xl">
          <Text>Profile not found.</Text>
        </Box>
      </Container>
    );
  }

  return (
    <Box py={"lg"}>
      <Container size="xl">
        <Stack gap="xl">
          {isEditing ? (
            // Check if we're editing a brand profile or a user profile
            profile.userType === "BRAND_USER" && profile.brandId ? (
              <BrandEditForm
                brand={{
                  id: profile.brandId,
                  name: profile.name || "",
                  logo: profile.imageUrl || "",
                  description: profile.description || "",
                  categories: profile.categories || [],
                  rating: profile.rating,
                  productsCount: 0,
                  completedCollabs: parseInt(profile.completedCollabs || "0"),
                  inviteCode: "", // Not needed for editing
                  status: "ACTIVE",
                  createdAt: profile.createdAt,
                  updatedAt: profile.updatedAt,
                }}
                onSave={handleBrandUpdate}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <ProfileEditForm
                profile={profile}
                onSave={handleProfileUpdate}
                onCancel={() => setIsEditing(false)}
                role={profile.userType}
              />
            )
          ) : (
            <>
              {/* Profile Header */}
              <ProfileHeader
                profile={profile}
                isMyProfile={isMyProfile}
                averageRating={averageRating}
                numReviews={numReviews}
                onEditClick={handleEditToggle}
                showTikTokButton={!userInfo?.brandId}
              />

              {/* Profile Tabs */}
              <Tabs defaultValue="info" variant="outline">
                <Tabs.List>
                  <Tabs.Tab value="info">Info & Stats</Tabs.Tab>
                  <Tabs.Tab value="reviews">Reviews ({numReviews})</Tabs.Tab>
                  {profile.userType === "BRAND_USER" && (
                    <Tabs.Tab value="products">Products ({numProducts})</Tabs.Tab>
                  )}
                  {profile.userType === "CREATOR" && (
                    <Tabs.Tab value="portfolio">Portfolio (0)</Tabs.Tab>
                  )}
                </Tabs.List>

                {/* Info & Stats Tab - Consolidated Information */}
                <Tabs.Panel value="info" pt="xl">
                  <ProfileInfoPanel profile={profile} />
                </Tabs.Panel>

                {/* Reviews Tab */}
                <Tabs.Panel value="reviews" pt="xl">
                  <Stack>
                    {numReviews > 0 ? (
                      profile.reviews?.map((review) => (
                        <ReviewCard key={review.id} review={review} />
                      ))
                    ) : (
                      <Text>No reviews yet.</Text>
                    )}
                  </Stack>
                </Tabs.Panel>

                {/* Products Tab (for Brands) */}
                {profile.userType === "BRAND_USER" && (
                  <Tabs.Panel value="products" pt="xl">
                    {productsLoading ? (
                      <Container className="center">
                        <Loader />
                      </Container>
                    ) : numProducts > 0 ? (
                      <SimpleGrid
                        cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                        spacing="md"
                      >
                        {productsData?.getAllProductsForBrand?.map((product) => (
                          <ProductCard key={product.id} product={product as Product} />
                        ))}
                      </SimpleGrid>
                    ) : (
                      <Text>No products yet.</Text>
                    )}
                  </Tabs.Panel>
                )}

                {/* Portfolio Tab (for Creators) */}
                {profile.userType === "CREATOR" && (
                  <Tabs.Panel value="portfolio" pt="xl">
                    <SimpleGrid
                      cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                      spacing="md"
                    >
                      <Text>No portfolio items yet.</Text>
                    </SimpleGrid>
                  </Tabs.Panel>
                )}
              </Tabs>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ProfilePage;
